import React, { useState } from 'react';
import { authApi } from '../../services/authApi';

interface Props {
  onSuccess: () => void;
}

export const SignIn: React.FC<Props> = ({ onSuccess }) => {
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!phone.match(/^[0-9]{10}$/)) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    // Local dev bypass — skip SMS/WhatsApp
    if (process.env.NODE_ENV === 'development') {
      localStorage.setItem('dd_session', 'dev-session');
      onSuccess();
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.sendPhone(phone);
      const sessionToken = res.data?.response?.sessionToken;
      if (!sessionToken) throw new Error('No session token received');
      localStorage.setItem('dd_session', sessionToken);
      onSuccess();
    } catch (err: any) {
      if (err.response?.status === 401) setError('Phone number not found in the system');
      else setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>Payro</div>
        <h1 style={styles.title}>Data Analysis</h1>
        <p style={styles.subtitle}>Enter your phone number to sign in</p>
        <form onSubmit={handleSubmit}>
          <input
            style={styles.input}
            type="tel"
            placeholder="05X-XXXXXXX"
            value={phone}
            onChange={e => { setPhone(e.target.value); setError(''); }}
            maxLength={10}
            autoFocus
          />
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '48px 40px',
    width: 380,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    textAlign: 'center',
  },
  logo: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 2,
    color: '#888',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1a1a2e',
    marginBottom: 8,
  },
  subtitle: {
    color: '#888',
    fontSize: 14,
    marginBottom: 32,
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    fontSize: 16,
    border: '1.5px solid #e0e0e0',
    borderRadius: 8,
    outline: 'none',
    marginBottom: 16,
    boxSizing: 'border-box',
  },
  button: {
    width: '100%',
    padding: '14px',
    background: '#1a1a2e',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
  },
  error: {
    color: '#e53e3e',
    fontSize: 13,
    marginBottom: 8,
    textAlign: 'left',
  },
};
